
'use client';
import { useState, useEffect, useRef } from 'react';

export default function Process() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  const stages = [
    {
      number: 1,
      name: 'Discover',
      tagline: 'Understanding Your Brand',
      description:
        'We analyze your brand, market position, and growth objectives through comprehensive research and stakeholder interviews.',
      icon: 'ri-search-eye-line',
      color: '#038C7F',
      details: [
        'Brand Audit',
        'Market Research',
        'Competitor Analysis',
        'Stakeholder Interviews',
      ],
    },
    {
      number: 2,
      name: 'Define',
      tagline: 'Strategic Direction',
      description:
        'We develop a strategic creative direction aligned with measurable outcomes and clear brand positioning.',
      icon: 'ri-compass-3-line',
      color: '#F2780C',
      details: [
        'Strategy Development',
        'Creative Brief',
        'Brand Positioning',
        'Goal Setting',
      ],
    },
    {
      number: 3,
      name: 'Design & Produce',
      tagline: 'Creative Execution',
      description:
        'We execute across digital, media, outdoor, or manufacturing platforms with precision and creativity.',
      icon: 'ri-pencil-ruler-2-line',
      color: '#038C7F',
      details: [
        'Visual Design',
        'Content Creation',
        'Production',
        'Quality Control',
      ],
    },
    {
      number: 4,
      name: 'Deliver & Scale',
      tagline: 'Launch & Growth',
      description:
        'We ensure seamless deployment and support your brand expansion with ongoing optimization.',
      icon: 'ri-rocket-2-line',
      color: '#F2780C',
      details: [
        'Deployment',
        'Launch Support',
        'Performance Tracking',
        'Scaling Strategy',
      ],
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible) {
      const interval = setInterval(() => {
        setActiveStep((prev) => (prev + 1) % stages.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [isVisible]);

  return (
    <section
      ref={sectionRef}
      id="process"
      className="relative py-12 sm:py-16 lg:py-20 bg-[#0D0D0D] overflow-hidden"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#038C7F]/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#F2780C]/30 to-transparent"></div>
        <div className="absolute top-16 left-8 sm:left-16 w-32 sm:w-48 h-32 sm:h-48 border border-[#038C7F]/10 rounded-full hidden sm:block"></div>
        <div className="absolute bottom-16 right-8 sm:right-16 w-24 sm:w-36 h-24 sm:h-36 border border-[#F2780C]/10 rotate-45 hidden sm:block"></div>
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage:
              'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        ></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div
          className={`text-center mb-8 sm:mb-10 lg:mb-12 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="inline-flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-1 sm:py-1.5 bg-white/5 border border-white/10 rounded-full mb-3 sm:mb-4">
            <i className="ri-flow-chart text-[#038C7F] text-sm sm:text-base"></i>
            <span className="text-[10px] sm:text-xs font-bold text-white/70 uppercase tracking-widest">
              Our Process
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-2 sm:mb-3">
            HOW WE{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#038C7F] to-[#F2780C]">
              WORK
            </span>
          </h2>
          <p className="text-sm sm:text-base text-gray-400 max-w-xl mx-auto px-4">
            A proven methodology that transforms ideas into impactful brand
            experiences
          </p>
        </div>

        {/* Process Timeline - Desktop */}
        <div className="hidden lg:block relative mb-10 lg:mb-12">
          {/* Progress Line */}
          <div className="absolute top-12 left-0 right-0 h-1 bg-white/10 rounded-full">
            <div
              className="h-full bg-gradient-to-r from-[#038C7F] to-[#F2780C] rounded-full transition-all duration-500"
              style={{
                width: `${((activeStep + 1) / stages.length) * 100}%`,
              }}
            ></div>
          </div>

          {/* Steps */}
          <div className="grid grid-cols-4 gap-4 lg:gap-6">
            {stages.map((stage, index) => (
              <div
                key={stage.number}
                onClick={() => setActiveStep(index)}
                className={`relative cursor-pointer transition-all duration-500 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                {/* Node */}
                <div className="flex justify-center mb-4 lg:mb-6">
                  <div
                    className={`relative w-20 h-20 lg:w-24 lg:h-24 rounded-full flex items-center justify-center transition-all duration-500 ${
                      index <= activeStep
                        ? 'bg-gradient-to-br shadow-xl scale-100'
                        : 'bg-white/5 scale-90'
                    }`}
                    style={{
                      backgroundImage:
                        index <= activeStep
                          ? `linear-gradient(135deg, ${stage.color}, ${stage.color}dd)`
                          : undefined,
                    }}
                  >
                    {index === activeStep && (
                      <div
                        className="absolute inset-0 rounded-full animate-ping opacity-20"
                        style={{ backgroundColor: stage.color }}
                      ></div>
                    )}
                    <i
                      className={`${stage.icon} text-2xl lg:text-3xl ${
                        index <= activeStep ? 'text-white' : 'text-white/30'
                      }`}
                    ></i>
                  </div>
                </div>

                {/* Content */}
                <div
                  className={`text-center transition-all duration-300 ${
                    index === activeStep ? 'opacity-100' : 'opacity-60'
                  }`}
                >
                  <div className="text-[10px] lg:text-xs font-bold text-[#038C7F] uppercase tracking-wider mb-1">
                    Step {stage.number}
                  </div>
                  <h3 className="text-lg lg:text-xl font-bold text-white mb-1">{stage.name}</h3>
                  <p className="text-[10px] lg:text-xs text-gray-400">{stage.tagline}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Steps - Horizontal Scroll */}
        <div className="lg:hidden mb-6 sm:mb-8 overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide">
          <div className="flex gap-3 min-w-max">
            {stages.map((stage, index) => (
              <button
                key={`mobile-tab-${stage.number}`}
                onClick={() => setActiveStep(index)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeStep === index
                    ? 'bg-white/10 border border-white/20'
                    : 'bg-white/5 border border-transparent'
                }`}
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: activeStep === index ? stage.color : `${stage.color}30` }}
                >
                  <i className={`${stage.icon} text-sm text-white`}></i>
                </div>
                <span className={`text-sm font-semibold ${activeStep === index ? 'text-white' : 'text-gray-400'}`}>
                  {stage.name}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Active Step Detail */}
        <div
          className={`transition-all duration-700 delay-300 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-10">
            <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 items-center">
              {/* Left - Visual */}
              <div className="relative order-2 lg:order-1">
                <div className="absolute -inset-2 sm:-inset-3 bg-gradient-to-r from-[#038C7F]/20 to-[#F2780C]/20 rounded-xl sm:rounded-2xl blur-xl"></div>
                <div className="relative bg-gradient-to-br from-white/10 to-white/5 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-white/10">
                  <div className="text-[80px] sm:text-[100px] lg:text-[140px] font-black leading-none text-transparent bg-clip-text bg-gradient-to-br from-white/20 to-transparent">
                    0{stages[activeStep].number}
                  </div>
                  <div
                    className="absolute top-4 sm:top-6 right-4 sm:right-6 w-12 h-12 sm:w-16 sm:h-16 rounded-lg sm:rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: stages[activeStep].color }}
                  >
                    <i
                      className={`${stages[activeStep].icon} text-2xl sm:text-3xl text-white`}
                    ></i>
                  </div>
                </div>
              </div>

              {/* Right - Content */}
              <div className="order-1 lg:order-2">
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                  <span
                    className="px-2.5 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-bold text-white"
                    style={{ backgroundColor: stages[activeStep].color }}
                  >
                    Phase {stages[activeStep].number}
                  </span>
                  <span className="text-xs sm:text-sm text-gray-400">
                    {stages[activeStep].tagline}
                  </span>
                </div>

                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-3 sm:mb-4">
                  {stages[activeStep].name}
                </h3>

                <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-4 sm:mb-6">
                  {stages[activeStep].description}
                </p>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                  {stages[activeStep].details.map((detail, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-white/5 rounded-lg border border-white/10"
                    >
                      <div
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: stages[activeStep].color }}
                      ></div>
                      <span className="text-xs sm:text-sm font-medium text-white">
                        {detail}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Steps List - Below Detail Card */}
        <div className="lg:hidden mt-6 space-y-2">
          {stages.map((stage, index) => (
            <div
              key={`mobile-list-${stage.number}`}
              onClick={() => setActiveStep(index)}
              className={`p-4 rounded-xl border transition-all duration-300 cursor-pointer ${
                activeStep === index
                  ? 'bg-white/10 border-white/20'
                  : 'bg-white/5 border-white/10'
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: stage.color }}
                >
                  <i className={`${stage.icon} text-lg sm:text-xl text-white`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] sm:text-xs font-bold text-gray-400 uppercase tracking-wider mb-0.5">
                    Step {stage.number}
                  </div>
                  <h4 className="text-sm sm:text-base font-bold text-white truncate">{stage.name}</h4>
                </div>
                <i
                  className={`ri-arrow-right-s-line text-lg sm:text-xl text-white/30 transition-transform flex-shrink-0 ${
                    activeStep === index ? 'rotate-90' : ''
                  }`}
                ></i>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
}
